/**
 * FINN-side reschedule on the Log — the moves that have no candidate behind them: an interviewer
 * who cannot make Tuesday, a panel that changed, a stage that slipped.
 *
 * Before this, those cost either two candidate emails and a spent booking token (cancel, then
 * re-invite) or a trip through the candidate's own manage page, which recorded FINN's decision as
 * the candidate's. It is the sibling of InterviewCancelButton and shares its two rules: a reason is
 * mandatory, and the server's atomic claim — never this dialog — is what guarantees one email.
 *
 * The reason stays INTERNAL. It goes on the calendar event and into the audit trail, because
 * "the interviewer can no longer make it" is ours to know; the candidate gets the optional sentence
 * the recruiter writes in the message field, and nothing else.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Input } from '@finn-auto/design-system/atoms/input';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../../../shared';
import {
  fetchBookingSlots,
  fetchRescheduleDefaultMessage,
  rescheduleInterviewBooking,
  STAFF_RESCHEDULE_REASONS,
} from '../services/interviewsApi';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import { SlotPicker } from './interviewBooking/SlotPicker';
import { groupByDay } from './interviewBooking/format';
import { LanguageToggle } from './taActionBoard/LanguageToggle';
import { ModalActions } from './taActionBoard/ModalActions';
import {
  berlinWallClockToIso,
  canSubmitReschedule,
  MAX_BODY_LENGTH,
  toRescheduleBody,
  type RescheduleFormValues,
  type RescheduleMode,
} from './rescheduleForm';

const TZ = 'Europe/Berlin';
const slotFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export function InterviewRescheduleButton({
  bookingRef,
  candidateName,
  currentStart,
  onRescheduled,
  surface = 'log',
}: {
  bookingRef: string;
  candidateName: string | null;
  currentStart: string | null;
  /** The list re-fetches, so the row shows the new time from real server state. */
  onRescheduled: () => void;
  /** Where the button sits — the Log or a request card — so the two are separable in analytics. */
  surface?: 'log' | 'requests' | 'board';
}) {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        Move…
      </Button>
    );
  }

  return (
    <Modal open onOpenChange={(next: boolean) => !next && setOpen(false)}>
      {/* Same overrides as the board's modals: below the DS md breakpoint ModalContent is a
          fullscreen sheet, which leaves a large empty band on a laptop. */}
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,36rem)] gap-5 overflow-auto rounded p-6">
        <RescheduleBody
          bookingRef={bookingRef}
          candidateName={candidateName}
          currentStart={currentStart}
          onClose={() => setOpen(false)}
          onRescheduled={onRescheduled}
          surface={surface}
        />
      </ModalContent>
    </Modal>
  );
}

/**
 * NOTE: deliberately NOT named "Modal…" — ModalContent clones any direct child whose name starts
 * with "Modal" to inject drawer props (see ActionModals).
 */
export function RescheduleBody({
  bookingRef,
  candidateName,
  currentStart,
  onClose,
  onRescheduled,
  surface,
}: {
  bookingRef: string;
  candidateName: string | null;
  currentStart: string | null;
  onClose: () => void;
  onRescheduled: () => void;
  surface: 'log' | 'requests' | 'board';
}) {
  const [mode, setMode] = useState<RescheduleMode>('slot');
  const [slotStart, setSlotStart] = useState('');
  /** Which day's times are shown. Null until the slots land, then the first day with any. */
  const [activeDayKey, setActiveDayKey] = useState<string | null>(null);
  const [specific, setSpecific] = useState('');
  const [reasonCode, setReasonCode] = useState('interviewer_unavailable');
  const [note, setNote] = useState('');
  /**
   * The candidate-facing email, pre-filled from the Email Library.
   *
   * It used to be a blank "optional message" box, so what a candidate read when
   * we moved their interview was whatever the recruiter had time to type — or,
   * far more often, the terse English sentence the code supplied when they typed
   * nothing. The wording is TA's now (Checking in → Reschedule), editable here
   * for this one email and in the Library for good.
   */
  const [emailBody, setEmailBody] = useState('');
  /**
   * The recruiter's OWN pick — null until they make one, which is the whole
   * point. Only an explicit choice is sent, so the proxy is free to derive the
   * default from the interview link (per role+stage). It used to start 'de'
   * and be sent every time, so the dialog opened German on every booking
   * whatever the role, and an English pipeline cost a click every time.
   */
  const [chosen, setChosen] = useState<'de' | 'en' | null>(null);
  /**
   * The language the box's text is actually in.
   *
   * Not always the one asked for: an empty German template falls back to the
   * English wording, and the email's frame has to follow the words. Sending the
   * requested language instead would put an English apology under "Wann:".
   *
   * Null until the proxy answers, because before that nobody knows — and the
   * toggle showing a guess would be a guess the recruiter has to undo.
   */
  const [bodyLanguage, setBodyLanguage] = useState<'de' | 'en' | null>(null);
  const [bodyTouched, setBodyTouched] = useState(false);
  /**
   * Whether to email the candidate the new time. Ticked by default; a recruiter who agreed the time
   * by phone can untick it. Their calendar invite still moves either way — this is only the email.
   */
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  // Availability is fetched when the dialog opens, not with the list: it costs a freeBusy call per
  // booking, and almost every row is never moved.
  const {
    data: availability,
    isPending: slotsPending,
    isFetching: slotsFetching,
    error: slotsError,
    refetch: refetchSlots,
  } = useQuery({
    queryKey: ['booking-slots', bookingRef],
    queryFn: () => fetchBookingSlots(bookingRef),
    // A slot list goes stale as calendars move; the server re-checks anyway, so a short life here
    // only decides how often the picker misleads before that refusal.
    staleTime: 60_000,
  });

  /**
   * The Library's wording for this booking, in the chosen language.
   *
   * Refetched on a language switch, and only while the dialog is open — a Log
   * page listing forty interviews must not ask the proxy forty times.
   */
  const { data: defaultMessage, isPending: defaultPending } = useQuery({
    queryKey: ['reschedule-default-message', bookingRef, chosen],
    queryFn: () => fetchRescheduleDefaultMessage(bookingRef, chosen ?? undefined),
    staleTime: 5 * 60_000,
  });

  /**
   * What the toggle shows: the recruiter's pick, else what the body IS.
   *
   * Null ONLY while the proxy has yet to answer — showing a guess there is a
   * guess the recruiter has to undo. Once the read has settled, the control is
   * always offered, falling back to 'en': a failed read leaves us knowing
   * nothing, and hiding the toggle then would let someone type German and send
   * it under an English frame with no way to say so.
   */
  const language = chosen ?? bodyLanguage ?? (defaultPending ? null : 'en');

  /* eslint-disable react-hooks/set-state-in-effect -- the default IS the box's
     initial content, and it arrives from the proxy after the dialog opens. */
  useEffect(() => {
    // Never over a recruiter's own edit: switching language to re-read the German
    // must not silently discard what they just wrote.
    if (bodyTouched) return;
    setEmailBody(defaultMessage?.body ?? '');
    setBodyLanguage(defaultMessage?.language ?? chosen ?? null);
  }, [defaultMessage, bodyTouched, chosen]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const days = useMemo(() => groupByDay(availability?.slots ?? []), [availability]);
  /**
   * Default to the first day that has times, but never override a day the recruiter picked — and
   * fall back if a refresh empties the day they were on (a colleague booking that morning).
   */
  const activeDay = days.find((d) => d.key === activeDayKey) ?? days[0] ?? null;

  const values: RescheduleFormValues = {
    mode,
    slotStart,
    specific,
    reasonCode,
    note,
    body: emailBody,
    /*
     * What the words ARE, not what was asked for — the frame follows the body.
     *
     * Untouched, the body IS the template, so its own language wins. Once the
     * recruiter has typed, the words are theirs and their explicit pick is the
     * better signal; the template they started from is the fallback. 'en' only
     * when neither is known, which is a failed read — and the toggle is on
     * screen showing EN in that case, so it can be corrected before sending.
     */
    language: (bodyTouched ? (chosen ?? bodyLanguage) : (bodyLanguage ?? chosen)) ?? 'en',
    sendEmail,
  };
  const typedIso = mode === 'specific' ? berlinWallClockToIso(specific) : null;
  const canSubmit = canSubmitReschedule(values) && !busy;

  const confirm = async () => {
    const body = toRescheduleBody(values);
    if (!body) return;
    setBusy(true);
    try {
      const result = await rescheduleInterviewBooking(bookingRef, body);
      if (result.alreadyMoved) {
        showToast({ status: 'info', description: 'That is already the booked time.' });
        onRescheduled();
        onClose();
        return;
      }
      if (!result.moved && !result.demo) throw new Error('The interview was not moved — try again.');
      // Categories and booleans only — never the candidate, the time or the message text (PII rule).
      void logAnalyticsEvent('interview_rescheduled_by_staff', {
        surface,
        mode,
        // The recruiter confirmed a time the calendars called busy (the 409 `clash` → override repeat).
        overrode_busy: Boolean(body.override),
        reason_code: reasonCode,
        availability_checked: result.checked ?? false,
        with_message: emailBody.trim().length > 0,
        // What the recruiter asked for, beside what actually went out.
        email_requested: sendEmail,
        email_sent: result.emailSent ?? false,
      });
      showToast({
        status: 'success',
        description: `${candidateName || 'The candidate'}'s interview has been moved${
          result.emailSent ? ' — they have been emailed' : ', but no email went out'
        }.`,
      });
      onRescheduled();
      onClose();
    } catch (e) {
      void logAnalyticsEvent('interview_rescheduled_by_staff_failed', { surface, mode, reason_code: reasonCode });
      showToast({
        status: 'error',
        description: e instanceof Error ? e.message : 'Could not move the interview.',
      });
    } finally {
      setBusy(false);
    }
  };


  return (
    <>
      <ModalHeader>
        <ModalTitle>Move {candidateName || 'this candidate'}’s interview</ModalTitle>
      </ModalHeader>

      <p className="text-sm text-gray-600">
        Currently{' '}
        <span className="font-medium text-gray-900">
          {currentStart ? slotFmt.format(new Date(currentStart)) : 'no time recorded'}
        </span>
        .{' '}
        {sendEmail
          ? 'The candidate is emailed the new time; the interviewer’s calendar invite moves with it.'
          : 'No email will be sent; the interviewer’s calendar invite still moves with it.'}
      </p>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="How to pick the new time">
        {(
          [
            { id: 'slot' as const, label: 'A free slot' },
            { id: 'specific' as const, label: 'A specific time' },
          ]
        ).map((m) => (
          <button
            key={m.id}
            type="button"
            aria-pressed={mode === m.id}
            disabled={busy}
            onClick={() => setMode(m.id)}
            className={cn(
              'rounded-full border px-3 py-1.5 text-xs font-medium',
              mode === m.id
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-500'
            )}
          >
            {m.label}
          </button>
        ))}
      </div>

      {mode === 'slot' ? (
        slotsPending ? (
          <p className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking the interviewers’ calendars…
          </p>
        ) : slotsError ? (
          <p className="text-sm text-red-700">
            Could not load available times. Use “A specific time” instead.
          </p>
        ) : days.length === 0 ? (
          /*
           * Deliberately NOT SlotPicker's own empty state: that one tells the reader to "contact
           * your recruiter", which is candidate copy and nonsense on a recruiter's screen.
           */
          <p className="text-sm text-amber-800">
            No free slots{availability?.slotError ? ` — ${availability.slotError}` : ''}. Use “A
            specific time” instead.
          </p>
        ) : (
          <SlotPicker
            days={days}
            activeDay={activeDay}
            onSelectDay={setActiveDayKey}
            onSelectSlot={(slot) => setSlotStart(slot.start)}
            selectedStart={slotStart || null}
            onRefresh={() => void refetchSlots()}
            refreshing={slotsFetching}
            heading="Pick a new time"
          />
        )
      ) : (
        <div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">
              New time ({TZ})
            </span>
            <Input
              type="datetime-local"
              value={specific}
              onChange={(e) => setSpecific((e.target as HTMLInputElement).value)}
              disabled={busy}
              aria-label={`New time (${TZ})`}
            />
          </label>
          <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              We will not check whether the interviewer is free — only use this when you have already
              agreed the time with them.
              {typedIso ? ` That is ${slotFmt.format(new Date(typedIso))}.` : ''}
            </span>
          </p>
        </div>
      )}

      <label className="block">
        <span className="mb-1 block text-xs font-medium text-gray-700">Why (internal)</span>
        <Select
          value={reasonCode}
          onValueChange={(next: string) => {
            setReasonCode(next);
            /**
             * Drop the note with the reason it belonged to. The input is only rendered for "Other",
             * but the STATE survived: pick Other, type "venue gone", switch to "The panel changed",
             * and the note is no longer validated yet still sent — the audit trail and the
             * interviewer's calendar then read "The panel or format changed — venue gone".
             */
            setNote('');
          }}
          disabled={busy}
        >
          <SelectTrigger aria-label="Reason for moving">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STAFF_RESCHEDULE_REASONS.map((r) => (
              <SelectItem key={r.code} value={r.code}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>

      {reasonCode === 'other' && (
        <Input
          value={note}
          onChange={(e) => setNote((e.target as HTMLInputElement).value)}
          disabled={busy}
          placeholder="Briefly, why?"
          aria-label="Reason note"
        />
      )}

      {/* NOT a <label>: the Checkbox has its own and the textarea its own aria-label — nested labels
          would be invalid. Unticking the box sends no email (the recruiter agreed the time already). */}
      <div className="block">
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <Checkbox
            label="Email the candidate the new time"
            labelClassName="text-xs font-medium text-gray-700"
            checked={sendEmail}
            disabled={busy}
            onCheckedChange={(v) => setSendEmail(v === true)}
          />
          {/* The candidate's language, not the recruiter's. Re-reads the Library
              in that language — and never over an edit already made. */}
          {/* No `hasEdits` guard: the re-fetch never overwrites a box the
              recruiter has typed in (see `bodyTouched`), so switching after an
              edit changes which language the FRAME uses and leaves the words
              alone. A confirm would warn about a loss that cannot happen. */}
          {sendEmail && language && (
            <LanguageToggle value={language} onChange={setChosen} disabled={busy} label="" />
          )}
        </div>
        {sendEmail ? (
          <>
            <Textarea
              value={emailBody}
              onChange={(e) => {
                setBodyTouched(true);
                setEmailBody((e.target as HTMLTextAreaElement).value);
              }}
              disabled={busy}
              rows={10}
              maxLength={MAX_BODY_LENGTH}
              aria-label="Email to the candidate"
            />
            <span className="mt-1 block text-xs text-gray-500">
              {/* Says where the two things it does NOT contain come from, because both
                  were reported missing when this box was blank. */}
              The new time and the rebooking link are added automatically underneath. The reason above
              stays internal — it goes on the calendar event, never in their email. Edit this wording
              for good in the Email Library, under <strong>Checking in → Reschedule</strong>.
            </span>
          </>
        ) : (
          <p className="text-xs text-amber-800">
            No email will go out from here. Make sure the candidate already knows the new time.
          </p>
        )}
      </div>

      {/*
        The picker stays on screen while the reason is filled in, so the chosen time is restated
        here in full. A highlighted chip alone reads as "10:00" with the day two rows above it —
        and the day is exactly the part someone moving an interview must not get wrong.
      */}
      {mode === 'slot' && slotStart ? (
        <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
          New time: <span className="font-medium text-gray-900">{slotFmt.format(new Date(slotStart))}</span>
        </p>
      ) : null}

      <ModalActions
        confirmLabel={busy ? 'Moving…' : sendEmail ? 'Move & email them' : 'Move the interview'}
        confirmDisabled={!canSubmit}
        cancelLabel="Keep the time"
        onConfirm={() => void confirm()}
        onCancel={onClose}
      />
    </>
  );
}
